import { useEffect, useRef, type ReactNode } from 'react';

import { Button } from './Button';
import { cx } from './cx';
import { Icon } from './Icon';

/**
 * The native `<dialog>`, opened with `showModal()`.
 *
 * Which buys, for nothing: focus trapped in the dialog, the rest of the document made inert,
 * Escape closing it, and the top layer — so it is never behind the map. A hand-rolled modal
 * reimplements all four and gets the first one wrong.
 *
 * It also matters to the keyboard shortcuts: `viewForKey` refuses the digits `1`–`3` while
 * a dialog is open, and it knows one is open by asking the document for `dialog[open]`.
 */
export function Dialog({
  open,
  title,
  onClose,
  actions,
  wide,
  className,
  children,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
  wide?: boolean;
  className?: string;
  children?: ReactNode;
}) {
  const element = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = element.current;

    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={element}
      onCancel={(event) => {
        // Escape reaches the element, not React's onClose — and left alone it would close
        // the dialog while the caller still believed it open.
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === element.current) onClose();
      }}
      className={cx(
        'm-auto p-0 bg-surface text-ink border border-rule rounded-card shadow-float backdrop:bg-scrim',
        wide ? 'w-[min(760px,92vw)]' : 'w-[min(440px,92vw)]',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 px-4 py-3 border-b border-rule">
        <h2 className="text-section font-semibold m-0">{title}</h2>
        <Button variant="quiet" size="sm" icon onClick={onClose} aria-label="Close">
          <Icon name="close" small />
        </Button>
      </header>
      <div className="px-4 py-3.5 max-h-[70vh] overflow-y-auto">{children}</div>
      {actions ? (
        <footer className="flex items-center justify-end gap-2 px-4 py-3 border-t border-rule">
          {actions}
        </footer>
      ) : null}
    </dialog>
  );
}
